<template>
  <div>
    <div class="calculator-container" :class="{ 'remove-margin': removeMargin }">
      <h1 class="h3">
        Mean, Median, Mode Calculator
      </h1>
      <form @submit.prevent="calculations">
        <div class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Enter Data
            </label>
            <div class="icon-info-tooltip mean-median-mode">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="decimal-places">
          <div class="label-tooltip">
            <label>
              Decimal Places
            </label>
            <div class="icon-info-tooltip mean-median-mode">
              <IconInfo />
              <Tooltip>
                <span>Choose the number of decimal places.</span>
              </Tooltip>
            </div>
          </div>
          <Dropdown
            calculator
            calculator-decimal-places
            class="small-dropdown"
            :selected="selectedDecimalPlaces"
            :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
            @change="currentSelectedDecimalPlace"
          />
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div
      v-show="median" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div>
          <p class="result-name col-8 sm-8">
            Mean (Arithmetic Average):
          </p>
          <p class="result-value">
            {{ Number(mean).toFixed(selectedDecimalPlaces) }}
          </p>
        </div>
        <div class="middle">
          <p class="result-name col-8 sm-8">
            Median:
          </p> <p class="result-value">
            {{ median }}
          </p>
        </div>
        <div :class="{ 'unset-overflow': mode === 'No Mode' }">
          <p class="result-name col-8 sm-8">
            Mode:
          </p>
          <div class="mode-overflow">
            <p class="result-value">
              {{ mode }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';

export default {
  components: {
    Dropdown, Tooltip, IconInfo,
  },
  data() {
    return {
      rawDataMeanArray: [],
      rawDataMean: '',
      textareaAlert: false,
      selectedDecimalPlaces: '4',
      mean: 0,
      median: '',
      mode: '',
      countsObject: {},
      removeMargin: false,
    };
  },
  watch: {
    rawDataMean() {
      this.median = '';
      this.defaultingInput();
    },
    selectedDecimalPlaces() {
      this.median = '';
      this.$store.commit('calculators/showSteps', false);
      this.removeMargin = false;
      this.textareaAlert = '';
    },
  },
  methods: {
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'summary';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    mounted() {
      this.$store.commit('calculators/showSteps', false);
    },
    reset(e) {
      this.rawDataMean = '';
      this.selectedDecimalPlaces = '4';
      this.defaultingInput();

      e.preventDefault();
    },
    defaultingInput() {
      this.removeMargin = false;
      this.$store.commit('calculators/showSteps', false);
      this.textareaAlert = '';
    },
    loadExample(e) {
      this.textareaAlert = '';
      this.median = '';
      this.selectedDecimalPlaces = '4';
      this.removeMargin = false;
      this.rawDataMean = '12\n9\n8\n1\n2\n3\n5\n5\n6\n7';
      this.$store.commit('calculators/showSteps', false);

      e.preventDefault();
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    inputValidation() {
      this.textareaAlert = '';

      let validationResult = true;
      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        validationResult = false;
      } else {
        for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
          if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
            this.textareaAlert = 'Invalid value';
            validationResult = false;
          }
        }
      }

      return validationResult;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      this.mean = 0;
      let size = 0;
      let firstPosition = 0;
      let secondPosition = 0;
      let even = true;

      // Mean calculation
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        this.mean += Number(this.rawDataMeanArray[i]);
      }
      this.mean = (this.mean / this.rawDataMeanArray.length).toFixed(this.selectedDecimalPlaces);

      // Median calculation
      this.rawDataMeanArray.sort((a, b) => a - b);
      this.rawDataMeanArray = this.rawDataMeanArray.map((str) => Number(str));
      size = this.rawDataMeanArray.length;

      if (size % 2 !== 0) {
        even = false;
      }

      if (even) {
        firstPosition = size / 2;
        secondPosition = (size + 2) / 2;
        this.median = (this.rawDataMeanArray[firstPosition - 1] + this.rawDataMeanArray[secondPosition - 1]) / 2;
      } else {
        this.median = this.rawDataMeanArray[((size + 1) / 2) - 1];
      }

      // Mode calculation
      const getMode = (arr) => {
        this.countsObject = {};
        let resultArr = [];

        for (let i = 0; i < arr.length; i += 1) {
          const item = arr[i];

          if (this.countsObject[item]) {
            this.countsObject[item] += 1;
          } else {
            this.countsObject[item] = 1;
          }
        }

        if (Object.values(this.countsObject).filter((count) => count > 1).length === 0) {
          return 'No Mode';
        }

        resultArr = Object.entries(this.countsObject).sort((a, b) => b[1] - a[1]);
        resultArr = resultArr.map((keyValue) => (resultArr[0][1] === keyValue[1] ? Number(keyValue[0]) : ''));
        resultArr = resultArr.filter((el) => el !== '');

        return resultArr;
      };

      const getModeResult = getMode(this.rawDataMeanArray);

      if (getModeResult === 'No Mode') {
        this.mode = 'No Mode';
      } else if (getModeResult.length === 1) {
        this.mode = Number(getModeResult.join('')).toFixed(this.selectedDecimalPlaces);
      } else {
        this.mode = getModeResult.join(',');
      }

      // Formatting Result
      this.median = this.median.toFixed(this.selectedDecimalPlaces);

      let meanMedianModeArray = [this.mean, this.median, this.mode];
      const onlyZerosTest = /^\d+.[0]+$/;
      const decimalTest = /^\d+\.{1}?\d+$/;

      meanMedianModeArray = meanMedianModeArray.map((str) => (onlyZerosTest.test(str) ? Math.trunc(str) : str));
      [this.mean, this.median, this.mode] = meanMedianModeArray.map((str) => (decimalTest.test(str) ? Number(str).toFixed(this.selectedDecimalPlaces) : str));

      if (Number.isNaN(Number(Math.trunc(this.mode)))) {
        let modeArray = this.mode.split(',');
        modeArray = modeArray.map((str) => (decimalTest.test(str) ? Number(str).toFixed(this.selectedDecimalPlaces) : str));
        this.mode = modeArray.join(',');
      }

      this.$store.commit('calculators/showSteps', true);
      this.calculationData(firstPosition, secondPosition);
      this.removeMargin = true;
      this.scrollToSection();
    },
    calculationData(firstPosition, secondPosition) {
      const onlyZerosTest = /^\d+.[0]+$/;
      const decimalTest = /^\d+\.{1}?\d+$/;
      let originalInputArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');
      originalInputArray = originalInputArray.map((str) => (onlyZerosTest.test(str) ? Math.trunc(str).toString() : str));
      originalInputArray = originalInputArray.map((str) => (decimalTest.test(str) ? Number(str).toFixed(this.selectedDecimalPlaces) : str));

      this.$store.commit('calculators/calculationData', {
        mean: Number(this.mean).toFixed(2),
        mode: this.mode,
        median: Number(this.median).toFixed(2),
        selectedDecimalPlaces: this.selectedDecimalPlaces,
        firstPosition,
        secondPosition,
        rawDataMeanArray: originalInputArray,
        countsObject: this.countsObject,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .decimal-places {
    width: calc(50% - 15px);
    margin-top: var(--spacing-30);
    margin-bottom: var(--spacing-36);
  }
}

.summary-container {
  .results {
    div {
      .mode-overflow {
        height: auto;
        overflow: auto;
        padding: 0 0 5px;

        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--accent);
          opacity: 0.7;
          border-radius: 10px;
        }
      }

      &.unset-overflow {
        overflow: unset;

        .mode-overflow {
          height: unset;
          overflow: unset;
          padding: 0;
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    margin-bottom: var(--spacing-100);

    &.remove-margin {
      margin-bottom: 0;
    }

    .decimal-places {
      .label-tooltip .icon-info-tooltip {
        &.mean-median-mode {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.mean-median-mode {
          &:hover {
            .tooltip {
              left: 130%;

              &:after {
                left: 42.15%;
              }
            }
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      width: 100%;

      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.mean-median-mode {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.mean-median-mode {
          &:hover {
            .tooltip {
              left: 271%;

              &:after {
                left: 34.5%;
              }
            }
          }
        }
      }
    }
  }

  .summary-container {
    .results {
      div:nth-child(3) {
        overflow: auto;
        overflow-y: hidden;

        &::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--accent);
          opacity: 0.7;
          border-radius: 10px;
        }

        .mode-overflow {
          overflow: unset;
          padding: 0;
        }

        &.unset-overflow {
          overflow: unset;
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
